// ContractPricing scheme ↔ amount-shape consistency check.
//
// Mirrors the database CHECK constraint
// `pricing_scheme_amount_consistent` (see
// `packages/prisma/prisma/migrations/README.md §12`):
//
//   MONTHLY  → totalMonthly  required, totalOneTime forbidden
//   ONE_TIME → totalOneTime  required, totalMonthly forbidden
//   PARTIAL  → totalMonthly + deposit required (totalOneTime optional)
//
// Lifted from `apps/api/src/contracts/contracts.service.ts` so the
// Windows app's ContractCreateWizard (PR 5-G2) can validate
// pricing rows client-side without round-tripping to the API. The
// server-side service imports this and wraps the returned error
// message in `BadRequestException` — same validation, single
// source of truth.
import { PricingScheme } from './enums.js';
/**
 * Returns `null` if the pricing shape is consistent, otherwise an
 * operator-readable error string explaining the violation.
 *
 * 문구는 **화면에 그대로 나가는 한국어**다(2026-08-31, 오너 지적) — 콘솔 마법사
 * 배너와 서버 400 응답 둘 다 이 문자열을 사람에게 보여준다. 종전의
 * "MONTHLY scheme requires totalMonthly…" 는 내부 칸 이름을 영어로 노출해
 * 운영자가 무엇을 하라는 건지 알 수 없었다. 칸 이름은 화면 라벨(월 합계/일시불/
 * 보증금)과 같은 말을 쓴다 — 화면과 오류가 다른 언어를 쓰면 안내가 아니라 수수께끼다.
 *
 * Pure function — no exceptions, no I/O. Server-side caller wraps
 * the non-null return in `BadRequestException`; client-side caller
 * renders it inline as a form-level banner.
 *
 * BigInt-string acceptance (`bigint | string`) lets the same
 * function check wire-format payloads (where BigInt rides as JSON
 * string per `decisions.md §C-6`) and parsed bigint values
 * interchangeably. Empty strings + `null` + `undefined` all count
 * as "absent".
 */
export function checkPricingShape(p) {
    const has = (v) => v !== null && v !== undefined && v !== '';
    if (p.scheme === PricingScheme.MONTHLY) {
        if (!has(p.totalMonthly) || has(p.totalOneTime)) {
            return "월 단위 계약은 '월 합계'를 입력해야 하고, '일시불' 칸은 비워야 합니다.";
        }
        return null;
    }
    if (p.scheme === PricingScheme.ONE_TIME) {
        if (!has(p.totalOneTime) || has(p.totalMonthly)) {
            return "일시불 계약은 '일시불'을 입력해야 하고, '월 합계' 칸은 비워야 합니다.";
        }
        return null;
    }
    if (p.scheme === PricingScheme.PARTIAL) {
        if (!has(p.totalMonthly) || !has(p.deposit)) {
            return "'일부(보증금+월)' 계약은 '월 합계'와 '보증금'을 모두 입력해야 합니다.";
        }
        return null;
    }
    return `알 수 없는 가격 방식입니다: ${p.scheme}`;
}
//# sourceMappingURL=contract-pricing.js.map